import { model as i18n } from '!/i18n'
import { register } from '!/repository'
import { model as tvShowSeasonsModel } from '$/shows/seasons'
import { aye, exists, F, nay, T } from '@/helpers'
import { extendMediaItemByLabelPrice } from '@/tools'
import { api, type TvShow, type TvShowSeason } from '@setplex/tria-api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
  type Effect,
  type Store,
} from 'effector'
import { not } from 'patronum'
import { model as popupModel } from '~/entities/info-popup'
import { model as tvShowModel } from '~/entities/shows'
import { model as paymentModel } from '~/features/payment/generic'
import * as pinLockFeature from '~/features/pin-lock'
import type { TvShowEpisodePayment } from '~/features/payment/interfaces'
import {
  getEpisodePaymentExternalId,
  getSeasonPaymentExternalId,
} from '~/features/payment/lib'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import {
  siginInToWatchFeatureModel,
  type SignInToWatchItem,
} from '~/features/signin-to-watch'
import { model as guestModel } from '~/features/subscriber/guest'
import { type TvShowRecommendedWithPrice } from '~/shared/interfaces'
import type { ITvShowEpisodeFormat } from './index.h'
import {
  checkUpdatedItem,
  checkUpdatedItemBundleIds,
  formatTVShowEpisode,
  formatTVShowEpisodesLabel,
  formatTVShowSeasonEpisodesLabel,
  formatTVShowSeasonsLabel,
} from './lib'

// *
// * EVENTS
// *

export const init = createEvent<{
  showId: string
  seasonId?: string | null
  query: URLSearchParams
}>()

const $initParams = restore(init, null)

export const left = createEvent()

const $isOnThePage = createStore(false).on(init, T).on(left, F)

// *
// * EFFECTS
// *

export const waitExactShowPageFx: Effect<
  {
    showId: string
    seasonId?: string
    query: URLSearchParams
  },
  TvShow[]
> = createEffect()

export const setCategoryId = createEvent<number>()
export const setShowIndex = createEvent<number>()

// *
// * STORES
// *

export const $recommended: Store<TvShowRecommendedWithPrice[]> = combine(
  api.tvshows.$recommendedTvShows,
  ssePurchasesModel.$tvShow,
  i18n.$t,
  (shows, adjust) =>
    shows.map((tvShow) =>
      extendMediaItemByLabelPrice(adjust(tvShow))
    ) as TvShowRecommendedWithPrice[]
)

// current tv show season
export const $season = createStore<TvShowSeason | null>(null)
  .on(tvShowSeasonsModel.$seasonDetails, (__, payload) => payload)
  .reset(init)

// store with tv show episodes
export const $episodes: Store<ITvShowEpisodeFormat[]> = combine(
  api.tvshows.$tvShowSeasonEpisodes,
  ssePurchasesModel.$episode,
  i18n.$t,
  formatTVShowEpisode
)

// TvShow seasons label
export const $seasonsLabel: Store<string> = combine(
  tvShowModel.$currentShow,
  i18n.$t,
  formatTVShowSeasonsLabel
)

// TvShow episodes label
export const $episodesLabel: Store<string> = combine(
  tvShowModel.$currentShow,
  i18n.$t,
  formatTVShowEpisodesLabel
)

export const $seasonEpisodesLabel: Store<string> = combine(
  $episodes,
  i18n.$t,
  formatTVShowSeasonEpisodesLabel
)

export const $seasonId = createStore<number | null>(null)
export const $episodeId = createStore<number | null>(null)

// *
// * CONNECTIONS
// *
const reloadPage = createEvent<void>()

// reload page if current show or season is updated
sample({
  clock: [ssePurchasesModel.$tvShowIds, ssePurchasesModel.$seasonIds],
  source: {
    currentShow: tvShowModel.$currentShow,
    season: $season,
  },
  filter: ({ currentShow, season }, ids) =>
    checkUpdatedItem({ currentShow, season, ids }),
  target: reloadPage,
})

// reload page if current show or season is updated
sample({
  clock: ssePurchasesModel.$tvShowSetIds,
  source: {
    currentShow: tvShowModel.$currentShow,
    season: $season,
  },
  filter: ({ currentShow, season }, ids) =>
    checkUpdatedItemBundleIds({ currentShow, season, ids }),
  target: reloadPage,
})

sample({
  clock: reloadPage,
  source: { initParams: $initParams, isOnThePage: $isOnThePage },
  filter: ({ initParams, isOnThePage }) => aye(initParams) && isOnThePage,
  fn: ({ initParams }) => initParams!,
  target: init,
})

//
// tv show itself
//
sample({
  clock: init,
  fn: ({ showId }) => ({ tvShowId: Number(showId) }),
  target: api.tvshows.getTvShowFx,
})

//
// episodes
//

// load all episodes
sample({
  clock: api.tvshows.getTvShowFx.doneData,
  filter: (shows) => nay(shows?.[0]?.isWithSeason),
  fn: ([{ id }]) => ({ tvShowId: id, page: 0 }),
  target: api.tvshows.getTvShowEpisodesWithoutSeasonByPageFx,
})

//
// seasons
//

// load all seasons
sample({
  clock: api.tvshows.getTvShowFx.doneData,
  filter: (shows) => aye(shows?.[0]?.isWithSeason),
  fn: ([{ id }]) => ({ tvShowId: id, page: 0 }),
  target: api.tvshows.getTvShowSeasonsByPageFx,
})

// if `seasonId` parameter is specified -> load requested season
sample({
  clock: init,
  source: api.tvshows.getTvShowSeasonByIdFx.pending,
  filter: (pending, { seasonId }) => nay(pending) && aye(seasonId),
  fn: (_, { showId, seasonId }) => ({
    tvShowId: Number(showId),
    seasonId: Number(seasonId),
    page: 0,
  }),
  target: api.tvshows.getTvShowSeasonByIdFx,
})

sample({
  clock: init,
  source: api.tvshows.getTvShowEpisodesBySeasonByPageFx.pending,
  filter: (pending, { seasonId }) => nay(pending) && aye(seasonId),
  fn: (_, { showId, seasonId }) => ({
    tvShowId: Number(showId),
    seasonId: Number(seasonId),
    page: 0,
  }),
  target: api.tvshows.getTvShowEpisodesBySeasonByPageFx,
})

// otherwise load first season
sample({
  clock: api.tvshows.getTvShowSeasonsByPageFx.doneData,
  source: init,
  filter: ({ seasonId }, { content: seasons }) =>
    nay(seasonId) && seasons && seasons.length > 0,
  fn: ({ showId }, { content: seasons }) => ({
    tvShowId: Number(showId),
    seasonId: seasons[0].id,
    page: 0,
  }),
  target: [
    api.tvshows.getTvShowEpisodesBySeasonByPageFx,
    api.tvshows.getTvShowSeasonByIdFx,
  ],
})

// sign in to watch logic

const selectTvShowEpisode = createEvent<
  TvShowEpisodePayment & SignInToWatchItem
>()

sample({
  clock: selectTvShowEpisode,
  source: {
    guest: guestModel.$guest,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && !free && !purchaseInfo,
  fn: (_, episode) => ({
    name: episode.name,
    redirect: episode.redirect,
  }),
  target: siginInToWatchFeatureModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvShowEpisode,
  source: {
    guest: guestModel.$guest,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && (aye(free) || aye(purchaseInfo)),
  fn: (_, episode) => episode,
  target: paymentModel.selectTvShowEpisode,
})

sample({
  clock: selectTvShowEpisode,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, episode) => episode,
  target: paymentModel.selectTvShowEpisode,
})

//
// recommendations
//

// load recommendations by `showId` and `categoryId`, if any
sample({
  clock: init,
  filter: not(api.tvshows.getRecommendedTvShowsFx.pending),
  fn: ({ showId, query }) => ({
    tvShowId: Number(showId),
    categoryId: Number(query.get('categoryId') || 0),
    showIndex: query.has('showIndex')
      ? Number(query.get('showIndex'))
      : undefined,
  }),
  target: api.tvshows.getRecommendedTvShowsFx,
})

export const $queryShowIndex = createStore<string>('').on(
  setShowIndex,
  (_, showIndex) => (exists(showIndex) ? `&showIndex=${showIndex}` : '')
)

export const $queryCategoryId = createStore<string>('').on(
  setCategoryId,
  (_, categoryId) => (exists(categoryId) ? `&categoryId=${categoryId}` : '')
)

export const $episodeQuery = api.tvshows.$tvShowSeasonEpisodes.map(
  (episodes) => {
    const id = episodes && episodes?.length > 0 ? episodes[0].id : null
    return exists(id) ? `&episodeId=${id}` : ''
  }
)

export const $seasonQuery = $season.map((season) => {
  const id = season?.id
  return exists(id) ? `&seasonId=${id}` : ''
})

const $seasonPaymentExternalId = combine(
  $season,
  tvShowModel.$currentShow,
  (season, show) => getSeasonPaymentExternalId({ season, show }) ?? null
)

const $episodePaymentExternalId = combine(
  tvShowModel.$currentShow,
  $season,
  (show, season) => (episode: ITvShowEpisodeFormat) =>
    getEpisodePaymentExternalId({ show, season, episode })
)

//
// Unlock pin locked movie
//

const unlock = createEvent()

sample({
  clock: unlock,
  source: api.tvshows.$currentTvShow,
  filter: Boolean,
  fn: ({ id, name, locked }) =>
    ({
      ...pinLockFeature.emptyPinLockState,
      id,
      name,
      locked,
    }) as pinLockFeature.IPinLockPopupProps,
  target: pinLockFeature.openPinLockPopup,
})

//
//
//

// *
// * SKIN
// *

// clear stores
api.tvshows.$currentTvShow.reset(left)
api.tvshows.$tvShowSeasonEpisodes.reset([init, left])
api.tvshows.$recommendedTvShows.reset(left)
sample({
  clock: init,
  target: popupModel.closeInfoPopup,
})

register(tvShowModel.$currentShow, '$shows/current')
register($seasonQuery, '$shows/showId/season')
register($episodeQuery, '$shows/showId/episode')
register($queryCategoryId, '$shows/showId/categoryId')
register($queryShowIndex, '$shows/showId/showIndex')
register($recommended, '$shows/showId/recommended')
register($queryCategoryId, '$shows/showId/categoryId')
register($queryShowIndex, '$shows/showId/showIndex')
register($season, '$shows/showId/seasonId/details')
register(
  paymentModel.selectTvShowSeasonPaymentButton,
  'shows/showId/selectTvShowSeasonPaymentButton'
)
register($episodes, '$shows/showId/index/episodes')
register(api.tvshows.$tvShowSeasons, '$shows/showId/index/seasons')
register($seasonId, '$shows/showId/seasonId')
register($episodeId, '$shows/showId/episodeId')
register($episodeQuery, '$shows/showId/episode')
register($seasonsLabel, '$shows/showId/seasonsLabel')
register($episodesLabel, '$shows/showId/episodesLabel')
register($seasonEpisodesLabel, '$shows/showId/seasonEpisodesLabel')
register(selectTvShowEpisode, 'selectTvShowEpisode')
register($seasonPaymentExternalId, '$shows/showId/seasonPaymentExternalId')
register($episodePaymentExternalId, '|shows/showId/episodePaymentExternalId')
register(unlock, 'routes/shows/details/unlock')
